import React from 'react';

import { withRouter } from 'react-router-dom';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { FileArchiveContext } from 'contexts/FileArchiveContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
	getClassificationColumns,
	getClassificationSettings,
	saveClassificationSettings,
	classifyFile,
	getFileExampleUrl,
} from 'utils/apiRequests';
import UploadStepsHeader from '../components/UploadStepsHeader';
import UploadData from '../components/UploadData';
import SpendCubeGeneration from '../components/SpendCubeGeneration';
import ConfigureHarmonisationStep from '../components/ConfigureHarmonisationStep';
import ConfigureClassificationStep from '../components/ConfigureClassificationStep';
import { UPLOAD_STATUS, FILE_TYPES } from '../constants';
import { MessageWithAction } from 'components/MessageWithAction';
import {
	Wrapper,
	StepContainer,
	UploadWrapper,
	ExampleDownload,
	HeaderPlaceholder,
	MapTaxonomyContainer,
	MapTaxonomyButton,
	MapTaxonomyText,
	DownloadLink,
} from './DirectIndirectSpend.styles';
import isEqual from 'lodash/isEqual';
import { LoaderWrapper } from '../Data.styles';
import FileList from '../components/FileList';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';

export class DirectIndirectSpend extends React.Component {
	state = {
		progressValue: 0,
		data: {
			columns: [],
		},
		activeStepIndex: 0,
		columnsLoading: false,
		isConfigLoading: false,
		isFileUploading: false,
	};
	scrollToHeaderRef = React.createRef();

	componentDidMount() {
		this.fetchConfigData();
	}

	fetchConfigData = () => {
		this.fetchClassificationConfig().then(this.getColumns);
	};

	getColumns = () => {
		const { status, isDirect } = this.props;

		if (!status.isDataPublished && !isDirect && status && status.fileName) {
			this.fetchClassificationColumns();
		}
	};

	updateState = updates => {
		this.setState({ ...updates });
	};

	updateDataState = updates => {
		this.setState({ data: { ...this.state.data, ...updates } });
	};

	componentDidUpdate(prevProps, prevState) {
		const { status, isDirect, fetchFileArchive } = this.props;
		const { data, activeStepIndex } = this.state;
		const { status: prevStatus } = prevProps;
		const { data: prevData } = prevState;

		if (
			!status.isDataPublished &&
			!isEqual(status, prevStatus) &&
			status.state === UPLOAD_STATUS.NO_DATA
		) {
			fetchFileArchive();
			this.fetchConfigData();
		}

		if (
			status &&
			!isDirect &&
			!status.isDataPublished &&
			status.fileName &&
			status.fileName !== prevStatus.fileName
		) {
			this.fetchClassificationColumns();
		}

		if (
			!isEqual(prevStatus, status) ||
			(activeStepIndex !== 2 && !isEqual(data, prevData))
		) {
			this.setFileUploadingState(false);
			const newActiveStepIndex = this.getActiveStepIndex();
			if (newActiveStepIndex !== activeStepIndex) {
				this.setState({ activeStepIndex: newActiveStepIndex });
			}
		}
	}

	fetchClassificationConfig() {
		this.setState({ isConfigLoading: true });

		return getClassificationSettings().then(config => {
			if (config) {
				this.setState({
					data: config,
					isConfigLoading: false,
				});
			}
		});
	}

	fetchClassificationColumns() {
		const { status } = this.props;

		this.setState({ columnsLoading: true });

		return getClassificationColumns(status.fileName).then(columns => {
			if (columns) {
				const { data } = this.state;

				this.setState({
					data: {
						...this.state.data,
						columns: columns.map(column => {
							const configColumn =
								(data.columns &&
									data.columns.find(item => item.name === column.name)) ||
								{};

							return { ...column, isSelected: configColumn.isSelected };
						}),
					},
					columnsLoading: false,
				});
			}
		});
	}

	handleNextStep = () => {
		this.setState({ activeStepIndex: this.state.activeStepIndex + 1 });
	};

	handleStepHeaderClick = activeStepIndex => {
		this.setState({ activeStepIndex });
	};

	handleHarmonization = () => {
		const { isDirect, refetch, status } = this.props;

		if (!isDirect) {
			this.handleNextStep();

			return;
		}

		const { data } = this.state;

		saveClassificationSettings(data).then(() =>
			classifyFile(status.fileName, isDirect)
				.then(() => {
					refetch();
				})
				.catch(() => {
					refetch();
				})
		);
	};

	handleClassification = () => {
		const { status, refetch, isDirect } = this.props;
		const { data } = this.state;

		this.setState({ activeStepIndex: 3 });

		saveClassificationSettings(data).then(() =>
			classifyFile(status.fileName, isDirect)
				.then(() => {
					refetch();
				})
				.catch(() => {
					refetch();
				})
		);

		this.scrollToHeaderRef &&
			this.scrollToHeaderRef.current &&
			this.scrollToHeaderRef.current.scrollIntoView(false);
	};

	getActiveStepIndex() {
		let activeStepIndex = 0;
		const { status, isDirect } = this.props;
		const { data } = this.state;

		switch (status.state) {
			case UPLOAD_STATUS.VALIDATED:
				if (!status.errors || status.errors.length === 0) {
					activeStepIndex = !data.isAlreadyExists ? 1 : isDirect ? 3 : 2;
				}
				break;
			case UPLOAD_STATUS.CLASSIFYING:
				activeStepIndex = 3;
				break;
			default:
				activeStepIndex = 0;
		}

		return activeStepIndex;
	}

	navigateToUnpublishedData = () => {
		const { history } = this.props;

		history.push('/unpublished');
	};

	setFileUploadingState = isFileUploading => {
		this.setState({ isFileUploading });
	};

	getFileListData = () => {
		const { fileArchive, isDirect } = this.props;

		return fileArchive.filter(
			({ fileType }) =>
				fileType.value ===
				(isDirect
					? FILE_TYPES.UNCLASSIFIED_DIRECT
					: FILE_TYPES.UNCLASSIFIED_INDIRECT)
		);
	};

	onUploadCompleted = () => {
		const { clearApiCache, fetchFileArchive, refetch } = this.props;

		refetch();
		fetchFileArchive();

		if (clearApiCache) {
			clearApiCache();
		}
	};

	render() {
		const {
			data,
			columnsLoading,
			activeStepIndex,
			isConfigLoading,
			isFileUploading,
		} = this.state;
		const { status, isDirect, permissions, refetch } = this.props;
		const showHarmonisationStep = !data.isAlreadyExists;
		const isInitialUpload =
			!isFileUploading &&
			(status.state === UPLOAD_STATUS.NO_DATA ||
				status.state === UPLOAD_STATUS.PUBLISHED);

		if (isConfigLoading) {
			return (
				<LoaderWrapper>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoaderWrapper>
			);
		}

		if (status.isUnpublishedDataExists) {
			return (
				<Wrapper>
					<MessageWithAction
						title="Unpublished data in the Spendcube"
						content="There is unpublished data ready for review. You must review and publish this data before uploading a new file."
						button={{
							text: 'Review data',
							color: 'orange',
							onClick: this.navigateToUnpublishedData,
						}}
					/>
				</Wrapper>
			);
		}
		const fileList = this.getFileListData();
		return (
			<Wrapper>
				<div ref={this.scrollToHeaderRef} />
				<UploadWrapper>
					{isInitialUpload ? (
						<HeaderPlaceholder />
					) : (
						<UploadStepsHeader
							activeStepIndex={activeStepIndex}
							onStepClick={this.handleStepHeaderClick}
							showHarmonisation={showHarmonisationStep}
							status={status}
							isDirect={isDirect}
						/>
					)}

					{activeStepIndex === 0 && (
						<UploadData
							status={status}
							newhistory={this.props.newhistory}
							normalUrlInfo={this.props.normalUrlInfo || undefined}
							fetchState={refetch}
							isDirect={isDirect}
							isHarmonised={!showHarmonisationStep}
							setFileUploadingState={this.setFileUploadingState}
						/>
					)}
					{showHarmonisationStep && (
						<StepContainer visible={activeStepIndex === 1}>
							<ConfigureHarmonisationStep
								onChange={this.updateState}
								data={data}
								onSubmit={this.handleHarmonization}
							/>
						</StepContainer>
					)}

					<StepContainer visible={activeStepIndex === 2}>
						<ConfigureClassificationStep
							onChange={this.updateDataState}
							loading={columnsLoading}
							data={data.columns}
							onSubmit={this.handleClassification}
						/>
					</StepContainer>
					{activeStepIndex === 3 && (
						<SpendCubeGeneration
							fetchState={refetch}
							onUploadCompleted={this.onUploadCompleted}
							isDirect={isDirect}
						/>
					)}
				</UploadWrapper>
				{permissions.includes(PERMISSIONS.TaxonomyApply) &&
					fileList.length > 0 && (
						<MapTaxonomyContainer>
							<MapTaxonomyButton
								onClick={() => {
									const { history } = this.props;
									const url = isDirect
										? `/taxonomymapper/direct?prevPageUrl=${
												history.location.pathname
										  }`
										: `/taxonomymapper/indirect?prevPageUrl=${
												history.location.pathname
										  }`;
									history.push(url);
								}}
							>
								Map Taxonomy
							</MapTaxonomyButton>
							<MapTaxonomyText>
								{isDirect
									? `
								Please map the client taxonomies into a unified project taxonomy
								using the taxonomy mapper
								`
									: `Please create Project taxonomy with taxonomy mapper`}
							</MapTaxonomyText>
						</MapTaxonomyContainer>
					)}
				<FileList data={fileList} />
				<ExampleDownload>
					<span>
						Please be sure you are uploading an Unclassified Raw Client Data in
						CSV format. You can download an example of the expected format
						below.
					</span>
					<DownloadLink
						href={getFileExampleUrl()}
						target="_self"
						download="template.csv"
					>
						Download example
					</DownloadLink>
				</ExampleDownload>
			</Wrapper>
		);
	}
}

export default withRouter(props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<ApiDataContext.Consumer>
				{({ clearApiCache }) => (
					<UploadStateContext.Consumer>
						{({ status, refetch }) => (
							<FileArchiveContext.Consumer>
								{({ fileArchive, refetch: fetchFileArchive }) => (
									<DirectIndirectSpend
										status={status}
										refetch={refetch}
										fileArchive={fileArchive}
										fetchFileArchive={fetchFileArchive}
										clearApiCache={clearApiCache}
										permissions={permissions}
										{...props}
									/>
								)}
							</FileArchiveContext.Consumer>
						)}
					</UploadStateContext.Consumer>
				)}
			</ApiDataContext.Consumer>
		)}
	</PermissionsContext.Consumer>
));
